/**
 * MCQ Set Schema
 * A content type that composes multiple MCQ elements
 */

import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { MCQElementData } from '../mcq/schema';
import type { BlockContent } from '../block/types';

/**
 * MCQ Set Item - a single question in the set
 */
export interface MCQSetItem {
    id: string;
    type: 'mcq';
    data: MCQElementData;
    layout?: ItemLayout;
}

/**
 * Per-item layout override
 */
export interface ItemLayout {
    alignment?: 'left' | 'center' | 'right';
    spacing?: 'none' | 'sm' | 'md' | 'lg';
    hidden?: boolean;
}

/**
 * MCQ Set Data - the content data
 */
export interface MCQSetData extends CompositeQuestionsLayoutFields {
    title?: string;
    /** Description content (supports text, image, audio, video, rich-text) */
    description?: BlockContent;
    items: MCQSetItem[];
}

/**
 * MCQ Set Content - complete content item
 */
export interface MCQSetContent {
    id: string;
    type: 'mcq-set';
    version: string;
    data: MCQSetData;
    config?: MCQSetConfig;
}

/**
 * Layout configuration for MCQ Set
 */
export interface MCQSetLayoutConfig extends LayoutConfig {
    /** Single page (`all`) or multi-page with tab navigation (`tabs`). Legacy `step` is treated as `tabs`. */
    displayMode?: 'all' | 'step' | 'tabs';
    /** Show progress indicator */
    showProgress?: boolean;
    /** Show question numbers */
    showQuestionNumbers?: boolean;
    /** MCQ choice rows: standard = vertical + CSS type; adaptive = legacy horizontal grid + scaled text */
    answerChoicesPresentation?: 'standard' | 'adaptive';
}

/**
 * Scoring configuration for MCQ Set - extends base ScoringConfig
 */
export interface MCQSetScoringConfig extends ScoringConfig {
    /** Points per question (default: 1) */
    pointsPerQuestion?: number;
    /** Passing threshold (0-1) */
    passingThreshold?: number;
}

/**
 * MCQ Set Config - content-type specific configuration
 * Uses Omit to replace the base types with our extended versions
 */
export interface MCQSetConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: MCQSetLayoutConfig;
    scoring?: MCQSetScoringConfig;
}

/**
 * Default i18n translations for MCQ Set
 */
export const defaultMCQSetI18n = {
    en: {
        submit: 'Submit',
        submitAnswers: 'Submit Answers',
        retry: 'Try Again',
        next: 'Next',
        previous: 'Previous',
        correct: 'Correct!',
        incorrect: 'Incorrect',
        congratulations: '🎉 Congratulations!',
        keepPracticing: '📚 Keep Practicing',
        progress: 'Progress',
        answered: 'answered',
        question: 'Question',
        of: 'of',
        score: 'Score',
        attemptsLeft: 'attempts left',
        noQuestions: 'No questions available',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Question set title',
        descriptionLabel: 'Description (optional)',
        enterDescription: 'Enter description…',
        questionsListTitle: 'Questions',
        emptyQuestionsMsg: 'No questions yet. Add your first question.',
        addQuestion: 'Add question',
        multipleChoice: 'Multiple choice',
        untitledQuestion: 'Untitled question',
        selectCorrectAnswerError: 'Select at least one correct answer.',
        detailEmptyMessage: 'Select a question or add one to start editing.',
        removeQuestion: 'Remove question',
        questionPreview: 'Question',
        detailTitle: 'Question',
        addSection: 'Add section',
        removeSection: 'Remove section',
        sectionDetailTitle: 'Section',
        setDetailsTitle: 'Question set details',
    },
    vi: {
        submit: 'Gửi',
        submitAnswers: 'Gửi câu trả lời',
        retry: 'Thử lại',
        next: 'Tiếp theo',
        previous: 'Trước',
        correct: 'Chính xác!',
        incorrect: 'Chưa đúng',
        congratulations: '🎉 Chúc mừng!',
        keepPracticing: '📚 Tiếp tục luyện tập',
        progress: 'Tiến độ',
        answered: 'đã trả lời',
        question: 'Câu hỏi',
        of: 'trên',
        score: 'Điểm',
        attemptsLeft: 'lần thử còn lại',
        noQuestions: 'Không có câu hỏi nào',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Tiêu đề bộ câu hỏi',
        descriptionLabel: 'Mô tả (tuỳ chọn)',
        enterDescription: 'Nhập mô tả…',
        questionsListTitle: 'Câu hỏi',
        emptyQuestionsMsg: 'Chưa có câu hỏi. Hãy thêm câu hỏi đầu tiên.',
        addQuestion: 'Thêm câu hỏi',
        multipleChoice: 'Trắc nghiệm',
        untitledQuestion: 'Câu hỏi chưa có tiêu đề',
        selectCorrectAnswerError: 'Phải chọn ít nhất một đáp án đúng.',
        detailEmptyMessage: 'Chọn một câu hỏi hoặc thêm mới để bắt đầu chỉnh sửa.',
        removeQuestion: 'Xóa câu hỏi',
        questionPreview: 'Câu hỏi',
        detailTitle: 'Câu hỏi',
        addSection: 'Thêm mục',
        removeSection: 'Xóa mục',
        sectionDetailTitle: 'Mục',
        setDetailsTitle: 'Chi tiết bộ câu hỏi',
    },
};

/**
 * Default MCQ Set Config
 */
export const defaultMCQSetConfig: MCQSetConfig = {
    layout: {
        type: 'vertical',
        gap: '1.5rem',
        displayMode: 'all',
        showProgress: true,
        showQuestionNumbers: true,
        answerChoicesPresentation: 'standard',
    },
    behavior: {
        allowRetry: true,
        shuffleChoices: false,
        showCorrectAnswer: true,
        showExplanation: true,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: false,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultMCQSetI18n,
    },
};

/**
 * Default MCQ Set Data
 */
export const defaultMCQSetData: MCQSetData = {
    // EAA-2082: a new MCQ Set starts with an EMPTY title so the editor shows only the
    // placeholder. A non-empty default ("Question Set") was picked up by the question-bank page
    // as the content title and saved although nobody typed it.
    title: '',
    description: {
        type: 'text',
        text: '',
        variant: 'body',
        alignment: 'left',
    },
    items: [],
};

/**
 * MCQ Set Content Type Schema Definition
 */
export const mcqSetSchema = {
    type: 'mcq-set',
    title: 'MCQ Question Set',
    description: 'A collection of multiple choice questions',
    version: '1.0.0',
    category: 'assessment',
    icon: 'ListChecks',
    tags: ['quiz', 'questions', 'mcq', 'assessment'],
    defaultData: defaultMCQSetData,
    defaultConfig: defaultMCQSetConfig,
    i18n: defaultMCQSetI18n,
};
